import React, { PureComponent, Fragment } from 'react';
import { connect } from 'react-redux';
import Classnames from 'classnames';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { findDistinct } from '../../utils/functions.js';
import i18n from '../../i18n';
import CheckBox from './CheckBox.js';
import './TabGroup.css';

// Group of rows of subscriptions
class TabGroup extends PureComponent {

    static defaultProps = {
        level: 1,
        showThisMany: 10,
        showRollupIfMoreThan: 5,
        category: null,
        twoColumnLayout: false,
    };

    constructor(props) {
        super(props);

        // Set up local state
        this.state = {
            checked: false,
            showMore: false,
            hideContent: false,
        };
        this.handleShowMore = this.handleShowMore.bind(this);
        this.handleHideContent = this.handleHideContent.bind(this);
    }

    handleShowMore() {

        this.setState({
            showMore: !this.state.showMore,
        });

    }

    handleHideContent() {

        this.setState({
            hideContent: !this.state.hideContent,
        });

    }

    // Count how many subscriptions exist in all the hierarchy underneath the 
    // categeory
    countSubsInCategory(category){

        // Count the distinct number of rows (not subscriptions as there will be more if headers exist) - use the label to count
        let count = category.subscriptions && category.subscriptions.length > 0 ? findDistinct('label', category.subscriptions).length : 0;

        // Loop over each subcategory adding to the count. Each will recursively call
        // this method
        if (category.subCategories) {
            category.subCategories.forEach(subCategory => {

                count = count + this.countSubsInCategory(subCategory);

            });
        }
        return count;
    }

    groupSubscriptionsByName() {

        const {
            category
        } = this.props;

        const groupedSubs = [];

        if(category.subscriptions){
            // Loop over each subscription and add it to the grouped list
            // if it hasn't already been seen before. Otherwise just add it 
            // to the existing one with the different header
            category.subscriptions.forEach(subscription => {

                const existingGroup = groupedSubs.filter(i => i.name === subscription.name);

                if (existingGroup.length === 0) {
                    groupedSubs.push({
                        'name': subscription.name,
                        'label': subscription.label,
                        'grouping': subscription.Grouping,
                        'subscriptions': [
                            subscription
                        ],
                    });
                } else {
                    existingGroup[0].subscriptions.push(subscription);
                }

            });
        }

        return groupedSubs;
    }

    // Render the rows of the table format where rows are groups by Headers
    renderRows(headers, groupedSubs) {

        const {
            showThisMany,
        } = this.props;

        const {
            showMore,
        } = this.state;

        return (
            groupedSubs.map((row, index) => {

                // Only show showThisMany rows unless the user has clicked Show More
                if (showMore || index < showThisMany) {

                    return (
                        <tr key={row.name}>
                            <td className={Classnames('name-column', 'sticky', row.grouping ? 'indent': '')}>
                                {row.label}
                            </td>
                            {headers.map(header => {

                                // Find the matching sub for the current header
                                const matchedSub = row.subscriptions.filter(i => i.header === header);

                                if (matchedSub.length > 0) {
                                    return (
                                        <td key={matchedSub[0].apiField}>
                                            <CheckBox
                                                subscription={matchedSub[0]}
                                                showLabel={false}
                                            />
                                        </td>
                                    );
                                } else {
                                    return <td key={header}>&nbsp;</td>;
                                }
                            })}
                        </tr>
                    );

                }
                return null;
            })
        );
    }

    /**
     * Render the subscriptions into a table
    * @param {Array} header List of headers for the group
    */
    renderTable(headers) {

        return (

            <div className='table-wrap'>
                <table className='table'>
                    <thead>
                        <tr>
                            <th className='name-column header-column sticky' colSpan={1}></th>
                            {
                                headers.map(header => {
                                    return (
                                        <th className='check-column header-column' key={header}>{header}</th>
                                    );
                                })
                            }
                        </tr>
                    </thead>
                    <tbody>
                        {this.renderRows(headers, this.groupSubscriptionsByName())}
                    </tbody>
                </table>
            </div>

        );

    }

    // Set up the show more link if needed
    renderShowMore() {

        const {
            category,
            showThisMany,
        } = this.props;

        const {
            showMore,
        } = this.state;

        // Count the distinct number of rows (not subscriptions as there will be more if headers exist) - use the label to count
        const countOfSubs = category.subscriptions && category.subscriptions.length > 0 ? 
            findDistinct('label', category.subscriptions).length : 0;

        // Set up the Show More link. If the count of subscriptions is big enough then add it
        let showMoreContent;
        if (countOfSubs > showThisMany) {
            showMoreContent =
                <div
                    className='show-more'
                    onClick={this.handleShowMore}
                >
                    {showMore ?
                        <Fragment>
                            <div className='show-more-text'>{i18n.t('show-less')}</div>
                            <div className='show-more-icon'><FontAwesomeIcon icon="caret-down" rotation={180} size="2x" style={{ color: '#5b6770' }} /></div>
                        </Fragment>
                        : 
                        <Fragment>
                            <div className='show-more-text'>{i18n.t('show-more')}</div>
                            <div className='show-more-icon'><FontAwesomeIcon icon="caret-down" size="2x" style={{ color: '#5b6770' }} /></div>
                        </Fragment>
                    }
                </div>;
        }

        return showMoreContent;
    }

    // Render the header so group can collapse
    renderHeader(){

        const {
           level,
           category,
           showRollupIfMoreThan,
        } = this.props;

        const {
            hideContent,
        } = this.state;

        let titleText, subtitle;
        if(level === 2){
            titleText = <h2>{category.category}</h2>;

            // Render the subtitle if it exists for the Category 2
            subtitle = category.subtitle ? 
            <div className='subtitle'>{category.subtitle}</div>
            : null;

        } else if (level > 2){
            titleText = <h3>{category.category}</h3>;
        }

        // Count the number of subs on or below this level
        const numberBelowInHierarchy = this.countSubsInCategory(category);

        // Show the chevron if there are more in the current and subcategories that the allowed amount
        let headerIcon;
        if (numberBelowInHierarchy > showRollupIfMoreThan){
            headerIcon = 
            <div className='header-hover' onClick={this.handleHideContent}>
                { hideContent ?
                    <FontAwesomeIcon icon="chevron-down"  size="2x" style={{ color: '#5b6770' }} />
                    :
                    <FontAwesomeIcon icon="chevron-down" rotation={180} size="2x" style={{ color: '#5b6770' }} />
                }
            </div>;
        }

        return(
            level > 1 ?  
            <Fragment>
                <div className='collapsing-header'>
                    <div className='header-hover' onClick={this.handleHideContent}>
                        {titleText}
                    </div>
                    
                    {headerIcon}
                </div>
                {subtitle}
            </Fragment>
            : null
        );

    }

    renderSubscriptions() {
        const {
            category,
            twoColumnLayout,
        } = this.props;

        let subscriptionOutput;
        const subscriptions = category.subscriptions;

        // Render the subscriptions as simple check boxes (no table)
        if (subscriptions && subscriptions.length > 0) {
            subscriptionOutput = subscriptions.map((sub, index) => {

                // Render with an indent if the row has a grouping
                return (
                    <div
                        className={Classnames('check-rows')}
                        key={sub.apiField}>
                        <CheckBox
                            subscription={sub}
                            indent={sub.grouping}
                        />
                    </div>
                );
            });

            // Add in an extra <div> if there is an odd number of subs in a 
            // two-column layout. Makes the last border work
            if (subscriptionOutput.length % 2 !== 0 && twoColumnLayout){

                subscriptionOutput.push(<div
                    className={Classnames('check-rows')}
                    key={Math.random}>
                </div>)
            }
        }

       

        return <div className={Classnames('subscription-group', twoColumnLayout ? 'two-columns': '')}>
                {subscriptionOutput}
            </div>;
    }

    render() {

        const {
            category,
            level,
            twoColumnLayout,
        } = this.props;

        const {
            hideContent,
        } = this.state;

        // Get the headers for the group
        const headers = findDistinct('header', category.subscriptions);

        const first = 'Quarterly Factsheet';
        headers.sort(function (x, y) { return x === first ? -1 : y === first ? 1 : 0; });


        // Show the content if the hide flag isn't set
        let subscriptionOutput, showMore, subCategories;
        if (hideContent === false) {

            // Show all the subcategories (and any below that recursively)
            subCategories = category.subCategories ? category.subCategories.map(subCategory => {
                return (
                    <TabGroup
                        level={level + 1}
                        category={subCategory}
                        key={subCategory.category}
                        twoColumnLayout={twoColumnLayout}
                    />
                );
            })
            : null;

            if (headers.length > 0) {
                // Render the group as a table
                subscriptionOutput = this.renderTable(headers);
                showMore = this.renderShowMore();
            } else {
                // Render the group as rows (not using table)
                subscriptionOutput = this.renderSubscriptions(headers);
            }
        }

        return (
            <div className='tab-group' key={category.title}>
                {this.renderHeader()}
                {/* Show all the subscriptions */}
                {subscriptionOutput}
                {showMore}
                {subCategories}
            </div>
        );
    }

}

function mapStateToProps(state) {
    return {
        data: state.data,
        params: state.params,
    };
}

export default connect(mapStateToProps,
    {
    })(TabGroup);
